import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { getTemplates, saveTemplate, deleteTemplate } from '../../services/manipulatedService';
import { FormulaTemplate, Ingredient } from '../../services/mockData';

interface FormulaTemplatesProps {
  onSelectTemplate: (template: FormulaTemplate) => void;
  onSaveAsTemplate?: (formula: { formulaName: string; ingredients: Ingredient[]; posology: string; duration: number; quantity: number; unit: string; observations?: string }) => void;
}

const DEFAULT_TEMPLATES: Omit<FormulaTemplate, 'id' | 'nutritionistId' | 'createdAt'>[] = [
  {
    formulaName: 'Fórmula para Deficiência de Vitamina D',
    ingredients: [
      { name: 'Vitamina D3', quantity: 5000, unit: 'UI', purpose: 'Correção de deficiência' },
      { name: 'Magnésio', quantity: 200, unit: 'mg', purpose: 'Auxilia absorção' },
    ],
    posology: '1 cápsula ao dia, preferencialmente após o almoço',
    duration: 90,
    quantity: 90,
    unit: 'cápsulas',
    observations: 'Repetir exame após 3 meses para reavaliação',
  },
  {
    formulaName: 'Fórmula para Ganho de Massa Muscular',
    ingredients: [
      { name: 'BCAA', quantity: 5, unit: 'g', purpose: 'Recuperação muscular' },
      { name: 'Creatina', quantity: 3, unit: 'g', purpose: 'Força e potência' },
      { name: 'L-Glutamina', quantity: 5, unit: 'g', purpose: 'Recuperação' },
      { name: 'Vitamina D3', quantity: 2000, unit: 'UI', purpose: 'Suporte ósseo' },
    ],
    posology: 'Tomar 1 dose após o treino, diluída em água',
    duration: 60,
    quantity: 60,
    unit: 'doses',
  },
  {
    formulaName: 'Fórmula para Deficiência de Ferro',
    ingredients: [
      { name: 'Ferro', quantity: 30, unit: 'mg', purpose: 'Correção de anemia' },
      { name: 'Vitamina C', quantity: 500, unit: 'mg', purpose: 'Aumenta absorção' },
      { name: 'Ácido Fólico', quantity: 400, unit: 'mcg', purpose: 'Síntese de hemácias' },
    ],
    posology: '1 cápsula em jejum, 30 minutos antes do café da manhã',
    duration: 90,
    quantity: 90,
    unit: 'cápsulas',
    observations: 'Evitar tomar com leite ou café. Repetir hemograma após 2 meses',
  },
  {
    formulaName: 'Fórmula para Suporte Imunológico',
    ingredients: [
      { name: 'Vitamina C', quantity: 1000, unit: 'mg', purpose: 'Antioxidante' },
      { name: 'Zinco', quantity: 15, unit: 'mg', purpose: 'Imunidade' },
      { name: 'Vitamina D3', quantity: 2000, unit: 'UI', purpose: 'Modulação imune' },
      { name: 'Selênio', quantity: 100, unit: 'mcg', purpose: 'Antioxidante' },
    ],
    posology: '1 cápsula ao dia, preferencialmente no café da manhã',
    duration: 60,
    quantity: 60,
    unit: 'cápsulas',
  },
  {
    formulaName: 'Fórmula para Saúde Intestinal',
    ingredients: [
      { name: 'Lactobacillus acidophilus', quantity: 5000000000, unit: 'UFC', purpose: 'Probiótico' },
      { name: 'Bifidobacterium', quantity: 5000000000, unit: 'UFC', purpose: 'Probiótico' },
      { name: 'L-Glutamina', quantity: 5, unit: 'g', purpose: 'Integridade intestinal' },
    ],
    posology: '1 cápsula ao dia, em jejum, com água em temperatura ambiente',
    duration: 30,
    quantity: 30,
    unit: 'cápsulas',
    observations: 'Manter em geladeira. Tomar longe de alimentos quentes',
  },
];

export const FormulaTemplates: React.FC<FormulaTemplatesProps> = ({
  onSelectTemplate,
  onSaveAsTemplate,
}) => {
  const { user } = useAuth();
  const [savedTemplates, setSavedTemplates] = useState<FormulaTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'default' | 'saved'>('default');

  useEffect(() => {
    if (user) {
      loadSavedTemplates();
    }
  }, [user]);

  const loadSavedTemplates = async () => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      const templates = await getTemplates(user.uid);
      setSavedTemplates(templates);
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!user?.uid) return;
    if (!window.confirm('Tem certeza que deseja excluir este template?')) return;

    try {
      await deleteTemplate(user.uid, templateId);
      await loadSavedTemplates();
    } catch (error) {
      console.error('Erro ao deletar template:', error);
      alert('Erro ao deletar template');
    }
  };

  const templates: (FormulaTemplate | Omit<FormulaTemplate, 'id' | 'nutritionistId' | 'createdAt'>)[] = 
    activeTab === 'default' ? DEFAULT_TEMPLATES : savedTemplates;

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <button
          type="button"
          onClick={() => setActiveTab('default')}
          style={{
            padding: '8px 16px',
            fontSize: '12px',
            background: activeTab === 'default' ? '#0ea5e9' : '#ffffff',
            color: activeTab === 'default' ? '#ffffff' : '#0f172a',
            border: '1px solid #e2e8f0',
            borderRadius: '6px',
            cursor: 'pointer',
          }}
        >
          Templates Padrão
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('saved')}
          style={{
            padding: '8px 16px',
            fontSize: '12px',
            background: activeTab === 'saved' ? '#0ea5e9' : '#ffffff',
            color: activeTab === 'saved' ? '#ffffff' : '#0f172a',
            border: '1px solid #e2e8f0',
            borderRadius: '6px',
            cursor: 'pointer',
          }}
        >
          Meus Templates ({savedTemplates.length})
        </button>
      </div>

      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {loading ? (
          <p>Carregando templates...</p>
        ) : templates.length === 0 ? (
          <p style={{ textAlign: 'center', color: 'var(--page-text-muted)', padding: '20px' }}>
            Nenhum template salvo ainda.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {templates.map((template, index) => {
              const templateId = activeTab === 'default' ? `default-${index}` : (template as FormulaTemplate).id;
              return (
              <div
                key={templateId}
                style={{
                  padding: '16px',
                  background: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>
                      {template.formulaName}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--page-text-muted)', marginBottom: '8px' }}>
                      <div><strong>Ingredientes:</strong> {template.ingredients.map(i => `${i.name} (${i.quantity}${i.unit})`).join(', ')}</div>
                      <div style={{ marginTop: '4px' }}><strong>Posologia:</strong> {template.posology}</div>
                      <div style={{ marginTop: '4px' }}><strong>Duração:</strong> {template.duration} dias | <strong>Quantidade:</strong> {template.quantity} {template.unit}</div>
                    </div>
                  </div>
                  {activeTab === 'saved' && (template as FormulaTemplate).id && (
                    <button
                      type="button"
                      onClick={() => handleDeleteTemplate((template as FormulaTemplate).id)}
                      style={{
                        padding: '4px 8px',
                        background: 'var(--theme-danger-solid)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '11px',
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
                <Button
                  variant="secondary"
                  onClick={() => {
                    const templateData: FormulaTemplate = activeTab === 'default' 
                      ? {
                          id: `default-${index}`,
                          nutritionistId: user?.uid || '',
                          ...(template as Omit<FormulaTemplate, 'id' | 'nutritionistId' | 'createdAt'>),
                          createdAt: new Date().toISOString(),
                        }
                      : (template as FormulaTemplate);
                    onSelectTemplate(templateData);
                  }}
                  style={{ fontSize: '11px', padding: '4px 8px' }}
                >
                  Usar este Template
                </Button>
              </div>
            );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

